import React from 'react';
import { Box } from '@mui/system';
import Grid from '@mui/material/Grid';
import {
    Typography,
    Button,
    Card,
    CardContent,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
} from '@mui/material';
import Colors from "../../components/ui/Colors";
import { PuffLoader } from "react-spinners";
import { css } from "@emotion/react";
import { uploadOrderFile } from "../../utilityFunctions/WarehouseUtil";
import Papa from "papaparse";

const cssLoader = css`
  display: block;
  margin-left: auto;
  margin-right: auto;
`;



export default function CreateDeliveryOrder(props) {

    const { loginUser } = props;

    const [loading, setLoading] = React.useState(false);
    const [orderFile, setOrderFile] = React.useState(null);
    const [invoiceFile, setInvoiceFile] = React.useState(null);
    const [csvPreviewRows, setCsvPreviewRows] = React.useState([]);
    const [csvTotalRows, setCsvTotalRows] = React.useState(0);
    const [uploadAlert, setUploadAlert] = React.useState(null); // { severity: 'success'|'error'|'warning', message: string }

    const handleOrderUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setOrderFile(file);
        setCsvPreviewRows([]);
        setUploadAlert(null);

        if (file.name.toLowerCase().endsWith(".csv")) {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (results) => {
                    setCsvPreviewRows(results.data.slice(0, 5));
                    setCsvTotalRows(results.data.length);
                },
            });
        }
    };

    const handleInvoiceUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setInvoiceFile(file);
        setUploadAlert(null);
    };

    const deleteOrderFile = () => {
        setOrderFile(null);
        setCsvPreviewRows([]);
    };

    const deleteInvoiceFile = () => {
        setInvoiceFile(null);
    };



    const uploadOrders = async () => {
        setLoading(true);
        setUploadAlert(null);
        let success = true;
        let savedCount = null;

        // Upload order file if present
        if (orderFile) {
            const receivedOrder = await uploadOrderFile(orderFile, "order", loginUser.jwtToken);
            if (!receivedOrder) {
                success = false;
            } else {
                savedCount = Array.isArray(receivedOrder) ? receivedOrder.length : null;
            }
            deleteOrderFile();
        }

        // Upload invoice file if present
        if (invoiceFile) {
            const receivedInvoice = await uploadOrderFile(invoiceFile, "invoice", loginUser.jwtToken);
            if (!receivedInvoice) {
                success = false;
            }
            deleteInvoiceFile();
        }

        setLoading(false);

        if (!success) {
            setUploadAlert({ severity: "error", message: "Ocurrió un error al cargar los archivos. Inténtalo de nuevo." });
            return;
        }

        if (savedCount !== null && csvTotalRows > 0 && savedCount < csvTotalRows) {
            const skipped = csvTotalRows - savedCount;
            setUploadAlert({
                severity: "warning",
                message: `${savedCount} órdenes cargadas. ${skipped} ${skipped === 1 ? "orden ya existía" : "órdenes ya existían"} y ${skipped === 1 ? "fue omitida" : "fueron omitidas"} (duplicadas).`,
            });
            return;
        }

        setUploadAlert({ severity: "success", message: "Archivos cargados exitosamente." });
    };

    const previewHeaders = csvPreviewRows.length > 0 ? Object.keys(csvPreviewRows[0]) : [];

    return (
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%", height: "100%", paddingY: 4 }}>
            <Box sx={{ width: { xs: "95%", sm: "80%", md: "55%", lg: "45%" } }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
                        <PuffLoader size={100} color={Colors.secondary} css={cssLoader} />
                    </Box>
                ) : (
                    <Card elevation={3} sx={{ borderRadius: 3 }}>
                        <CardContent sx={{ padding: 4 }}>

                            {/* Page title */}
                            <Typography variant="h5" sx={{ fontWeight: "bold", color: Colors.primary, marginBottom: 1 }}>
                                Cargar Órdenes
                            </Typography>
                            <Divider sx={{ marginBottom: 3 }} />

                            {/* Order file section */}
                            <Box sx={{ marginBottom: 3 }}>
                                <Typography variant="subtitle2" sx={{ color: "text.secondary", marginBottom: 1 }}>
                                    Pedidos Clientes
                                </Typography>
                                {!orderFile ? (
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        sx={{ textTransform: "none", color: "gray", borderColor: "gray" }}
                                    >
                                        Seleccionar archivo
                                        <input
                                            type="file"
                                            hidden
                                            accept=".xlsx,.xls,.csv"
                                            onChange={handleOrderUpload}
                                        />
                                    </Button>
                                ) : (
                                    <Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                {orderFile.name}
                                            </Typography>
                                            <Button
                                                size="small"
                                                onClick={deleteOrderFile}
                                                sx={{ color: "error.main", textTransform: "none", minWidth: "auto" }}
                                            >
                                                Borrar
                                            </Button>
                                        </Box>

                                        {/* CSV preview table */}
                                        {csvPreviewRows.length > 0 && (
                                            <Box sx={{ marginTop: 2 }}>
                                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                    Vista previa (primeras {csvPreviewRows.length} filas)
                                                </Typography>
                                                <TableContainer component={Paper} variant="outlined" sx={{ marginTop: 1, maxHeight: 220 }}>
                                                    <Table size="small" stickyHeader>
                                                        <TableHead>
                                                            <TableRow>
                                                                {previewHeaders.map((h) => (
                                                                    <TableCell key={h} sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5", fontSize: "0.72rem" }}>
                                                                        {h}
                                                                    </TableCell>
                                                                ))}
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {csvPreviewRows.map((row, idx) => (
                                                                <TableRow key={idx}>
                                                                    {previewHeaders.map((h) => (
                                                                        <TableCell key={h} sx={{ fontSize: "0.72rem" }}>
                                                                            {row[h]}
                                                                        </TableCell>
                                                                    ))}
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            <Divider sx={{ marginBottom: 3 }} />

                            {/* Invoice file section */}
                            <Box sx={{ marginBottom: 3 }}>
                                <Typography variant="subtitle2" sx={{ color: "text.secondary", marginBottom: 1 }}>
                                    Factura Venta
                                </Typography>
                                {!invoiceFile ? (
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        sx={{ textTransform: "none", color: "gray", borderColor: "gray" }}
                                    >
                                        Seleccionar archivo
                                        <input
                                            type="file"
                                            hidden
                                            accept=".xlsx,.xls,.csv"
                                            onChange={handleInvoiceUpload}
                                        />
                                    </Button>
                                ) : (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                            {invoiceFile.name}
                                        </Typography>
                                        <Button
                                            size="small"
                                            onClick={deleteInvoiceFile}
                                            sx={{ color: "error.main", textTransform: "none", minWidth: "auto" }}
                                        >
                                            Borrar
                                        </Button>
                                    </Box>
                                )}
                            </Box>

                            {/* Upload button */}
                            {(orderFile || invoiceFile) && (
                                <Box sx={{ marginBottom: 2 }}>
                                    <Button
                                        onClick={uploadOrders}
                                        variant="contained"
                                        sx={{ backgroundColor: Colors.primary, textTransform: "none", paddingX: 4 }}
                                    >
                                        Cargar archivos
                                    </Button>
                                </Box>
                            )}

                            {/* Upload feedback alert */}
                            {uploadAlert && (
                                <Alert
                                    severity={uploadAlert.severity}
                                    onClose={() => setUploadAlert(null)}
                                    sx={{ marginTop: 2 }}
                                >
                                    {uploadAlert.message}
                                </Alert>
                            )}

                        </CardContent>
                    </Card>
                )}
            </Box>
        </Box>
    );
}
